import React from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter';
import { useState } from 'react';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { translate } from '../../../services/translate';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import MarkerDropdown from '../../../components/facial/MarkerDropdown';
import CamerasDropdown from '../../../components/facial/CamerasDropdown';
import PeopleDropdown from '../../../components/facial/PeopleDropdown';
import { View } from 'react-native';
import FilterInputNumeric from '../../../components/Filter/components/FilterInputNumeric';
import PersonTypeDropdown from '../../../components/facial/PersonTypeDropdown';
import { PersonType } from '../../../services/central-api/facial';

interface Props {
    filters: FacialParamList['Search'];
    setFilters: React.Dispatch<React.SetStateAction<FacialParamList['Search']>>;
}

export default function FacialSearchFilter({ filters, setFilters }: Props) {
    const navigation = useNavigation<NavigationProp<FacialParamList, 'Search'>>();

    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [onlyWithPerson, setOnlyWithPerson] = useState<boolean>(filters.onlyWithPerson ?? true);
    const [selectedMarkersIds, setSelectedMarkersIds] = useState<number[]>(filters.markers ?? []);
    const [selectedCamerasIds, setSelectedCamerasIds] = useState<string[]>(filters.cameras ?? []);
    const [selectedPersonId, setSelectedPersonId] = useState<number>(filters.personId ?? 0);
    const [confidence, setConfidence] = useState<number | null>(filters.confidence ?? null);
    const [personType, setPersonType] = useState<PersonType | ''>(filters.personType ?? '');
    const [personText, setPersonText] = useState<string>(filters.personText ?? '');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(7);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(6);

    const handleCleanFilter = () => {
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setOnlyWithPerson(true);
        setSelectedMarkersIds([]);
        setSelectedCamerasIds([]);
        setSelectedPersonId(0);
        setConfidence(null);
        setPersonType('');
        setPersonText('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            onlyWithPerson: onlyWithPerson,
            markers: selectedMarkersIds.length ? selectedMarkersIds : undefined,
            cameras: selectedCamerasIds.length ? selectedCamerasIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            personId: selectedPersonId !== 0 ? selectedPersonId : undefined,
            confidence: confidence ? confidence : undefined,
            personType: personType !== '' ? personType : undefined,
            personText: personText !== '' ? personText : undefined,
        });

        navigation.setParams({
            onlyWithPerson: onlyWithPerson,
            markers: selectedMarkersIds.length ? selectedMarkersIds : undefined,
            cameras: selectedCamerasIds.length ? selectedCamerasIds : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            personId: selectedPersonId !== 0 ? selectedPersonId : undefined,
            confidence: confidence ? confidence : undefined,
            personType: personType !== '' ? personType : undefined,
            personText: personText !== '' ? personText : undefined,
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('beginIn')}
                    zIndex={7}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(7)}
                />
            </View>
            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    zIndex={6}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(6)}
                />
            </View>
            <CamerasDropdown showLabel={true} setValue={setSelectedCamerasIds} value={selectedCamerasIds} zIndex={5} />
            <PeopleDropdown
                setValue={setSelectedPersonId}
                value={selectedPersonId}
                zIndex={4}
                personText={personText}
                setPersonText={setPersonText}
            />
            <View>
                <FilterCheckBox
                    label={translate('onlyByReference')}
                    value={onlyWithPerson}
                    onChange={(value) => {
                        setOnlyWithPerson(value);
                        if (!value) {
                            setPersonType('');
                        }
                    }}
                />
            </View>
            <MarkerDropdown setValue={setSelectedMarkersIds} value={selectedMarkersIds} zIndex={3} disabled={!onlyWithPerson} />
            <PersonTypeDropdown setValue={setPersonType} value={personType} zIndex={2} includeEmptyItem={true} type='read' disabled={!onlyWithPerson} />
            <FilterInputNumeric
                label={translate('confidence')}
                value={confidence}
                onChange={setConfidence}
                placeholder={translate('typeAValue')}
                maxValue={100}
                disabled={!onlyWithPerson && !selectedPersonId}
            />
        </Filters>
    );
}



